import React, { useCallback, useState } from 'react';

import { apiPost, apiPostAsUser, responseErrorMessage } from '../../lib/api_v2';
import { logError } from '../../lib/logger';
import { cn } from '../../lib/utils';

// The space needs a title at create, but the description is the user's note about what
// the space is for — not a name — and it reads badly in the sidebar. Ship a neutral
// placeholder; the identity job retitles the space the moment the real name lands, about
// two seconds later, and the confirm step only PATCHes if the user edits it.
const PLACEHOLDER_TITLE = 'New Space';

const MAX_DESCRIPTION_LENGTH = 1000;

const SUBMIT_LABEL = { idle: 'Create', creating: 'Creating' };

/**
 * Screen one of the AI space flow: the creator says what the space is for, and that one
 * sentence seeds the name, the cover photo, the wallpapers, and the first widget.
 *
 * The workspace is created here rather than after generation so the user lands in a real
 * space immediately and watches it fill in, instead of waiting on a blank modal.
 */
const NewSpaceDescribePanel = ({ onSpaceSetupStarted, workspaceUrlForWorkspace }) => {
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const trimmedDescription = description.trim();

  const handleDescriptionChange = useCallback(function handleDescriptionChangeCallback(event) {
    setDescription(event.target.value.slice(0, MAX_DESCRIPTION_LENGTH));
  }, []);

  const handleSubmit = useCallback(
    async function handleSubmitCallback(event) {
      event.preventDefault();
      if (isSubmitting || !trimmedDescription) return;

      setIsSubmitting(true);
      setErrorMessage(null);

      try {
        const response = await apiPostAsUser('/workspaces', {
          workspace: { title: PLACEHOLDER_TITLE },
        });
        const data = await response.json();

        if (!(response.ok && data.ok && data.data?.workspace?.id)) {
          setErrorMessage(responseErrorMessage(data, 'Could not create the space. Try again.'));
          return;
        }

        const createdWorkspacePayload = data.data;
        const workspace = createdWorkspacePayload.workspace;

        // Generation is the nice-to-have, the space is the deliverable: a failure here
        // still lands the user in a working space, just without the generated setup.
        try {
          await apiPost(`/workspaces/${workspace.id}/setup`, {
            workspace_setup: { description: trimmedDescription },
          });
        } catch (error) {
          logError('NewSpaceDescribePanel', 'Failed to start space setup', { message: error?.message });
        }

        await onSpaceSetupStarted(createdWorkspacePayload, workspaceUrlForWorkspace(workspace));
      } catch (error) {
        logError('NewSpaceDescribePanel', 'Failed to create space', { message: error?.message });
        setErrorMessage('Could not create the space. Try again.');
      } finally {
        setIsSubmitting(false);
      }
    },
    [isSubmitting, onSpaceSetupStarted, trimmedDescription, workspaceUrlForWorkspace],
  );

  return (
    <form onSubmit={handleSubmit} className="flex h-full w-full flex-col">
      {/* Mirrors SettingsBackButton on the opposite side, anchored to the Modal root. */}
      <button
        type="submit"
        disabled={isSubmitting || !trimmedDescription}
        className="absolute top-5 right-5 z-(--stack-modal-close) flex h-9 cursor-pointer items-center justify-center rounded-full bg-idle-blue px-4 text-[15px] leading-5 font-medium text-white hover:opacity-90 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500"
      >
        {isSubmitting ? SUBMIT_LABEL.creating : SUBMIT_LABEL.idle}
      </button>

      <div className="flex flex-1 flex-col justify-center pb-16">
        <h1 className="text-center text-[28px] leading-9 font-semibold tracking-[-0.02em] text-balance text-black dark:text-neutral-100">
          What&rsquo;s the space for?
        </h1>

        <label htmlFor="new-space-description" className="sr-only">
          What the space is for
        </label>
        <textarea
          id="new-space-description"
          value={description}
          onChange={handleDescriptionChange}
          disabled={isSubmitting}
          autoFocus
          rows={3}
          maxLength={MAX_DESCRIPTION_LENGTH}
          placeholder="Group golf trip with 6 people"
          data-1p-ignore="true"
          className={cn(
            'mt-8 w-full resize-none rounded-xl border border-white bg-gray-50 px-4 py-3.5 text-[15px] leading-6 text-black outline-none placeholder:text-gray-400 hover:bg-white focus:border-gray-400 disabled:cursor-wait sm:border-[0.5px]',
            'dark:border-white/10 dark:bg-neutral-900 dark:text-neutral-100 dark:hover:bg-neutral-900',
            errorMessage && 'border-red-300 focus:border-red-400',
          )}
        />
        {errorMessage ? <div className="mt-2 text-xs leading-5 font-medium text-red-600">{errorMessage}</div> : null}
      </div>
    </form>
  );
};

export default NewSpaceDescribePanel;
